// ─────────────────────────────────────────────
// KLOTHIQ — useSuppliers hook
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@klothiq/domain';

type Supplier = Database['public']['Tables']['suppliers']['Row'];
type SupplierInsert = Database['public']['Tables']['suppliers']['Insert'];

export const SUPPLIERS_KEY = 'suppliers';

export function useSuppliers(search?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SUPPLIERS_KEY, orgId, search],
    queryFn: async (): Promise<Supplier[]> => {
      let query = supabase
        .from('suppliers')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('name');

      if (search?.trim()) {
        query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%,city.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60,
  });
}

export function useSupplier(supplierId?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SUPPLIERS_KEY, orgId, supplierId],
    queryFn: async (): Promise<Supplier | null> => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('id', supplierId!)
        .eq('organization_id', orgId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId && !!supplierId,
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: Omit<SupplierInsert, 'organization_id' | 'created_by' | 'updated_by'>) => {
      let orgId = organization?.id;
      if (!orgId) {
        const { data: firstOrg } = await supabase.from('organizations').select('id').limit(1).single();
        orgId = firstOrg?.id;
      }
      if (!orgId) throw new Error('Organization not found. Please log in again.');

      const { data, error } = await supabase
        .from('suppliers')
        .insert({ ...values, organization_id: orgId, created_by: profile?.id, updated_by: profile?.id })
        .select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SUPPLIERS_KEY] }),
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...values }: Partial<Supplier> & { id: string }) => {
      const { data, error } = await supabase
        .from('suppliers')
        .update({ ...values, updated_by: profile?.id })
        .eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SUPPLIERS_KEY] }),
  });
}
